import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useAction,useMutation } from 'convex/react';
import { api } from './convex/_generated/api';

const GOOGLE_FONTS = `@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');`;

const SITUATIONS = {
  work: {
    label: "Work & Career", icon: "💼", color: "#C8A96E",
    situations: [
      { id: "raise", label: "Ask for a Raise", premium: true, pack: "raise", icon: "💰" },
      { id: "toxic-boss", label: "Handle a Toxic Boss", premium: true, pack: "manager", icon: "🛡️" },
      { id: "coworker-conflict", label: "Confront a Coworker", premium: false, icon: "🤝" },
      { id: "push-back", label: "Push Back on Manager", premium: true, pack: "manager", icon: "✋" },
      { id: "fire-employee", label: "Fire an Employee", premium: true, pack: "manager", icon: "📋" },
      { id: "feedback", label: "Give Hard Feedback", premium: true, pack: "manager", icon: "🎯" },
      { id: "resign", label: "Resign Gracefully", premium: false, icon: "🚪" },
      { id: "credit", label: "Take Credit for Your Work", premium: false, icon: "⭐" },
      { id: "deadline", label: "Miss a Deadline", premium: false, icon: "⏰" },
      { id: "promotion-denied", label: "Respond to Denied Promotion", premium: true, pack: "raise", icon: "📊" },
      { id: "salary-offer", label: "Negotiate a Job Offer", premium: true, pack: "raise", icon: "🤑" },
      { id: "bad-review", label: "Dispute a Bad Review", premium: true, pack: "raise", icon: "📝" },
    ]
  },
  personal: {
    label: "Relationships", icon: "❤️", color: "#E07A7A",
    situations: [
      { id: "breakup", label: "End a Relationship", premium: false, icon: "💔" },
      { id: "boundary", label: "Set a Boundary", premium: false, icon: "🚧" },
      { id: "end-friendship", label: "End a Friendship", premium: false, icon: "🌿" },
      { id: "family-limit", label: "Limit Family Contact", premium: true, pack: "relationships", icon: "🏠" },
      { id: "say-no", label: "Say No (Guilt-Free)", premium: false, icon: "🙅" },
      { id: "apology", label: "Apologize Meaningfully", premium: false, icon: "🕊️" },
      { id: "confront-friend", label: "Confront a Friend", premium: false, icon: "💬" },
      { id: "ask-space", label: "Ask for Space", premium: false, icon: "🌙" },
      { id: "cancel-plans", label: "Cancel Plans (Honestly)", premium: false, icon: "📅" },
      { id: "toxic-friend", label: "Cut Off a Toxic Person", premium: true, pack: "relationships", icon: "✂️" },
      { id: "money-owed", label: "Ask for Money Back", premium: false, icon: "💸" },
      { id: "confront-lying", label: "Confront Cheating/Lying", premium: true, pack: "relationships", icon: "🔍" },
    ]
  },
  tenant: {
    label: "Renter & Consumer", icon: "🏛️", color: "#7A9EC8",
    situations: [
      { id: "rent-increase", label: "Fight a Rent Increase", premium: true, pack: "tenant", icon: "📈" },
      { id: "landlord-repair", label: "Demand Repairs", premium: false, icon: "🔧" },
      { id: "deposit-return", label: "Get Deposit Back", premium: true, pack: "tenant", icon: "💵" },
      { id: "customer-service", label: "Escalate to a Supervisor", premium: false, icon: "📞" },
      { id: "cancel-service", label: "Cancel a Subscription", premium: false, icon: "✂️" },
      { id: "refund", label: "Demand a Refund", premium: false, icon: "↩️" },
      { id: "noisy-neighbor", label: "Confront a Noisy Neighbor", premium: false, icon: "🔊" },
      { id: "late-fees", label: "Dispute Late Fees", premium: false, icon: "🧾" },
      { id: "insurance-claim", label: "Fight an Insurance Denial", premium: true, pack: "tenant", icon: "📋" },
      { id: "contractor", label: "Dispute Bad Contractor Work", premium: true, pack: "tenant", icon: "🏗️" },
    ]
  },
  social: {
    label: "Social & Awkward", icon: "😬", color: "#9E7AC8",
    situations: [
      { id: "uninvite", label: "Uninvite Someone", premium: false, icon: "🚫" },
      { id: "overstay", label: "Tell Guest to Leave", premium: false, icon: "🏠" },
      { id: "wedding-drama", label: "Handle Wedding Drama", premium: true, pack: "relationships", icon: "💍" },
      { id: "ghosted", label: "Respond to Being Ghosted", premium: false, icon: "👻" },
      { id: "rude-comment", label: "Respond to Rude Comment", premium: false, icon: "🗣️" },
      { id: "borrowing", label: "Get Back Borrowed Item", premium: false, icon: "📦" },
      { id: "sensitive-topic", label: "Disagree on Politics/Religion", premium: true, pack: "relationships", icon: "⚖️" },
      { id: "oversharer", label: "Stop an Oversharer", premium: false, icon: "🤐" },
    ]
  }
};

const allsit= ["Ask for a Raise 💰", "End a Relationship 💔", "Fight Rent Increase 📈", "Handle Toxic Boss 🛡️", "Say No Guilt-Free 🙅", "Get Deposit Back 💵", "Confront a Friend 💬", "Resign Gracefully 🚪"];


const TONES = [
  { id: "firm", label: "Firm & Direct", desc: "Clear, no-nonsense", emoji: "💪" },
  { id: "warm", label: "Warm & Empathetic", desc: "Kind, understanding", emoji: "🤗" },
  { id: "professional", label: "Professional", desc: "Formal, measured", emoji: "👔" },
  { id: "assertive", label: "Assertive", desc: "Confident, decisive", emoji: "🦁" },
  { id: "calm", label: "Calm & Neutral", desc: "Non-reactive", emoji: "🧘" },
  { id: "humorous", label: "Light & Disarming", desc: "Soft entry point", emoji: "😊" },
];

const FORMATS = [
  { id: "script", label: "Spoken Script", icon: "🎙️" },
  { id: "email", label: "Email Draft", icon: "✉️" },
  { id: "text", label: "Text/SMS", icon: "💬" },
  { id: "bullet", label: "Talking Points", icon: "📋" },
];


const PACKS = [
  { id: "raise", title: "Ask for a Raise", price: "$9", includes: ["Opening script (3 tones)", "What if they say no", "Follow-up email", "Negotiation lines", "Do/Don't checklist"], color: "#C8A96E", popular: true },
  { id: "manager", title: "Difficult Manager", price: "$9", includes: ["Toxic boss confrontation", "Push back script", "Escalation email", "Paper trail language", "Exit interview lines"], color: "#E07A7A", popular: false },
  { id: "tenant", title: "Tenant Toolkit", price: "$9", includes: ["Rent negotiation script", "Repair demand letter", "Deposit recovery email", "Lease dispute language", "Escalation ladder"], color: "#7A9EC8", popular: false },
  { id: "relationships", title: "Relationship Reset", price: "$9", includes: ["Boundary-setting scripts", "Toxic person exit", "Family confrontation", "Cheating/lying confrontation", "Post-conflict repair"], color: "#9E7AC8", popular: false },
];



function getUserSituations(user) {
  if (user?.packRaise && user?.packManager && user?.packTenant && user?.packRelationships) {
    return SITUATIONS;
  }


  return Object.fromEntries(
    Object.entries(SITUATIONS).map(([key, cat]) => [
      key,
      {
        ...cat,
        situations: cat.situations.filter(sit =>
          !sit.premium ||
          (sit.pack === "raise" && user?.packRaise) ||
          (sit.pack === "manager" && user?.packManager) ||
          (sit.pack === "tenant" && user?.packTenant) ||
          (sit.pack === "relationships" && user?.packRelationships)
        )
      }
    ])
  );
}
function getUserMapped(user) {
  if (user?.packRaise && user?.packManager && user?.packTenant && user?.packRelationships) {
    return allsit;
  }
  
  if (user?.packManager) {
    return ["Ask for a Raise 💰", "Handle Toxic Boss 🛡️", "Resign Gracefully 🚪","Say No Guilt-Free 🙅", "Confront a Friend 💬"];
  }
  if (user?.packRaise) {
    return ["Ask for a Raise 💰", "Resign Gracefully 🚪","Say No Guilt-Free 🙅", "Confront a Friend 💬"];
  }
  if (user?.packTenant) {
    return ["Fight Rent Increase 📈", "Get Deposit Back 💵", "Confront a Friend 💬","Say No Guilt-Free 🙅","Resign Gracefully 🚪"];
  }
  if (user?.packRelationships) {
    return ["End a Relationship 💔", "Say No Guilt-Free 🙅", "Confront a Friend 💬","Resign Gracefully 🚪","Say No Guilt-Free 🙅"];
  }
  return ["Say No Guilt-Free 🙅", "Confront a Friend 💬", "Resign Gracefully 🚪"];
}


function userHasPack(user, packId) {
  if (packId === "raise" && user?.packRaise === true) return true;
  if (packId === "manager" && user?.packManager === true) return true;
  if (packId === "tenant" && user?.packTenant === true) return true;
  if (packId === "relationships" && user?.packRelationships === true) return true;
  return false;
}

function VoicePlayer({ text }) {
  const [speaking, setSpeaking] = useState(false);
  const [supported] = useState(() => "speechSynthesis" in window);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(0);
  const [rate, setRate] = useState(0.95);

  useEffect(() => {
    const load = () => {
      const v = window.speechSynthesis.getVoices().filter(v => v.lang.startsWith("en"));
      setVoices(v);
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.cancel(); };
  }, []);

  const speak = () => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    const clean = text.replace(/\*\*/g, "").replace(/---.*?---/g, "").trim();
    const utt = new SpeechSynthesisUtterance(clean);
    if (voices[selectedVoice]) utt.voice = voices[selectedVoice];
    utt.rate = rate;
    utt.onstart = () => setSpeaking(true);
    utt.onend = () => setSpeaking(false);
    utt.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utt);
  };

  const stop = () => { window.speechSynthesis.cancel(); setSpeaking(false); };
  if (!supported) return null;

  return (
    <div style={vs.container}>
      <div style={vs.label}>🔊 Listen to your script</div>
      <div style={vs.controls}>
        {voices.length > 0 && (
          <select value={selectedVoice} onChange={e => setSelectedVoice(+e.target.value)} style={vs.select}>
            {voices.slice(0, 8).map((v, i) => (
              <option key={i} value={i}>{v.name.split(" ").slice(0, 3).join(" ")}</option>
            ))}
          </select>
        )}
        <div style={vs.rateRow}>
          <span style={vs.rateLabel}>Speed</span>
          <input type="range" min="0.6" max="1.4" step="0.05" value={rate}
            onChange={e => setRate(+e.target.value)} style={vs.rangeInput} />
          <span style={vs.rateVal}>{rate.toFixed(2)}x</span>
        </div>
        <button onClick={speaking ? stop : speak}
          style={{ ...vs.btn, background: speaking ? "#E07A7A" : "#C8A96E" }}>
          {speaking ? "⏹ Stop" : "▶ Play"}
        </button>
      </div>
      {speaking && (
        <div style={vs.waveRow}>
          {[...Array(14)].map((_, i) => (
            <div key={i} style={{ ...vs.bar, animationDelay: `${i * 0.07}s` }} />
          ))}
        </div>
      )}
    </div>
  );
}

const vs = {
  container: { background: "#0F0F16", border: "1px solid #2a2a38", borderRadius: 12, padding: "16px 20px", marginTop: 20 },
  label: { fontSize: 11, fontWeight: 600, color: "#555", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 12 },
  controls: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  select: { background: "#1a1a24", border: "1px solid #2a2a38", borderRadius: 8, padding: "6px 10px", color: "#C0C0CC", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" },
  rateRow: { display: "flex", alignItems: "center", gap: 8 },
  rateLabel: { fontSize: 12, color: "#555" },
  rangeInput: { width: 80, accentColor: "#C8A96E" },
  rateVal: { fontSize: 12, color: "#888", width: 36 },
  btn: { border: "none", borderRadius: 8, padding: "7px 16px", color: "#0C0C0E", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" },
  waveRow: { display: "flex", alignItems: "flex-end", gap: 3, height: 24, marginTop: 12 },
  bar: { width: 4, height: 14, background: "#C8A96E", borderRadius: 2, animation: "waveBounce 0.6s ease-in-out infinite alternate" },
};

function HistoryPanel({ history, onRestore, onClear }) {
  const [open, setOpen] = useState(false);
  if (!history.length) return null;
  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px 8px" }}>
      <button onClick={() => setOpen(!open)}
        style={{ background: "#13131A", border: "1px solid #2a2a35", borderRadius: 8, padding: "8px 14px", color: "#555", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
        🕐 {history.length} saved script{history.length !== 1 ? "s" : ""} {open ? "▲" : "▼"}
      </button>
      {open && (
        <div style={{ background: "#13131A", border: "1px solid #2a2a35", borderRadius: 12, marginTop: 8, overflow: "hidden", animation: "fadeUp 0.2s ease both" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #1E1E28" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#555", textTransform: "uppercase", letterSpacing: "0.1em" }}>Recent Scripts</span>
            <button onClick={onClear} style={{ background: "none", border: "none", color: "#E07A7A", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Clear all</button>
          </div>
          {history.map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #1a1a22" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#D0D0DC", marginBottom: 2 }}>{item.situationLabel}</div>
                <div style={{ fontSize: 12, color: "#555", textTransform: "capitalize" }}>{item.tone} · {item.format} · {item.date}</div>
                {item.context && <div style={{ fontSize: 11, color: "#444", fontStyle: "italic", marginTop: 2 }}>"{item.context.slice(0, 60)}{item.context.length > 60 ? "..." : ""}"</div>}
              </div>
              <button onClick={() => { onRestore(item); setOpen(false); }}
                style={{ background: "transparent", border: "1px solid #2a2a35", borderRadius: 6, padding: "5px 12px", color: "#888", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", marginLeft: 12, flexShrink: 0 }}>
                Restore
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function ScriptResult({ result, tone, format }) {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("script");

  const handleCopy = () => {
    let text = "";
    if (activeTab === "donts") {
      text = [...(result.dos || []).map(d => "✓ " + d), ...(result.donts || []).map(d => "✗ " + d)].join("\n");
    } else {
      text = result[activeTab] || "";
    }
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!result) return null;
  const tabs = ["script", "variations", "responses", "donts"];
  const tabLabels = { script: "Main Script", variations: "Variations", responses: "If They Push Back", donts: "Do's & Don'ts" };

  return (
    <div style={S.resultContainer}>
      <div style={S.resultHeader}>
        <div style={{ display: "flex", gap: 8 }}>
          <span style={S.badge1}>{format.toUpperCase()}</span>
          <span style={S.badge2}>{tone.toUpperCase()}</span>
        </div>
        <button onClick={handleCopy} style={{ ...S.copyBtn, ...(copied ? { borderColor: "#4a9a6a", color: "#4a9a6a" } : {}) }}>
          {copied ? "✓ Copied!" : "Copy"}
        </button>
      </div>
      <div style={S.tabs}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{ ...S.tab, ...(activeTab === tab ? S.tabActive : {}) }}>
            {tabLabels[tab]}
          </button>
        ))}
      </div>
      <div style={{ padding: 24 }}>
        {activeTab === "donts" ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#4a9a6a", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.1em" }}>✅ DO</div>
              {(result.dos || []).map((item, i) => <div key={i} style={S.listItem}>{item}</div>)}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#E07A7A", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.1em" }}>❌ DON'T</div>
              {(result.donts || []).map((item, i) => <div key={i} style={S.listItem}>{item}</div>)}
            </div>
          </div>
        ) : (
          <div>
            {(result[activeTab] || "").split("\n").map((line, i) => (
              <p key={i} style={line.startsWith("**") ? S.scriptLabel : S.scriptLine}>
                {line.replace(/\*\*/g, "")}
              </p>
            ))}
            {activeTab === "script" && <VoicePlayer text={result.script || ""} />}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ScriptGenerator2() {
  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSituation, setSelectedSituation] = useState(null);
  const [selectedTone, setSelectedTone] = useState("firm");
  const [selectedFormat, setSelectedFormat] = useState("script");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [emailCapture, setEmailCapture] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  //const sessionId = location.state?.sessionId;
  const sessionId = localStorage.getItem('session_id');
  const resultRef = useRef(null);

  // ← get user from session
  const user = useQuery(api.users.getUserBySession,
    sessionId ? { sessionId } : "skip"
  );
  console.log(sessionId);
  console.log("user");
  console.log(user);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const createCheckout = useAction(api.stripe.createCheckoutSession);
  const createPortal = useAction(api.stripe.createPortalSession);
  const logoutUser = useAction(api.mfa.logoutUser);
  const deleteSession = useMutation(api.users.deleteSession);

  const generateScriptAction = useAction(api.mfa.generateScript);
  //const fetchScripts = useQuery(api.users.getUserScripts);
  const scripts = useQuery(
  api.users.getUserScripts,
  user?._id ? { userId: user._id } : "skip"
);

  const [history, setHistory] = useState(scripts ?? []);
  //const load = async () => {
    //    if (!user?._id) return;
     //   const scripts = await fetchScripts({ userId: user._id });
       // setHistory(scripts);
    //};

  const accessibleSituations = getUserSituations(user);
  const mapped= getUserMapped(user);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = GOOGLE_FONTS + `
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: #0C0C0E; }
      ::-webkit-scrollbar { width: 6px; }
      ::-webkit-scrollbar-track { background: #1a1a1f; }
      ::-webkit-scrollbar-thumb { background: #3a3a4a; border-radius: 3px; }
      @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes waveBounce { from { transform: scaleY(0.3); } to { transform: scaleY(1); } }
      @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      @keyframes glow { 0%,100% { box-shadow:0 0 20px rgba(200,169,110,0.1); } 50% { box-shadow:0 0 40px rgba(200,169,110,0.3); } }
      .sit-card:hover { border-color: #3a3a48 !important; transform: translateY(-2px) !important; background: #191922 !important; }
      .tone-card:hover { border-color: #3a3a48 !important; background: #191922 !important; }
      .hero-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(200,169,110,0.35); }
      .gen-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(200,169,110,0.3); }
    `;
    document.head.appendChild(style);
  }, []);

  
  //useEffect(() => {
    //if (user?._id){
    //load();
    //}
   //}, [user?._id]);

  const currentSituation = selectedCategory && selectedSituation
    ? accessibleSituations[selectedCategory]?.situations.find(s => s.id === selectedSituation)
    : null;

  const generateScript = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      const parsed = await generateScriptAction({
        userId: user?._id,
        situationLabel: currentSituation?.label || "",
        toneLabel: TONES.find(t => t.id === selectedTone)?.label || "",
        formatLabel: FORMATS.find(f => f.id === selectedFormat)?.label || "",
        context,
        isPremium: currentSituation?.premium || false,
        pack: currentSituation?.pack || null,
        tone: selectedTone,
        format: selectedFormat,
        date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        category: selectedCategory,
        situationId: selectedSituation,
      });

      setResult(parsed);
      //await load();
      //const newItem = {
        //situationLabel: currentSituation?.label,
        //tone: selectedTone,
        //format: selectedFormat,
        //context: context.slice(0, 100),
        //date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        //result: parsed,
        //category: selectedCategory,
        //situationId: selectedSituation,
      //};
      //setHistory(prev => [newItem, ...prev.slice(0, 19)]);
      setStep(4);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth" }), 150);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGetPack = async (packId) => {
  if (!user) { setError("Please sign in first"); navigate("/"); return; }
  setCheckoutLoading(packId);
  try {
    const { url } = await createCheckout({ userId: user._id, email: user.email, packId });
    window.location.href = url;
  } catch (err) {
    setError("Failed to start checkout");
  } finally {
    setCheckoutLoading(null);
  }
};

const handleLogout = async () => {
  try {
    await logoutUser({ sessionId });
    //if (sessionId) {
      //console.log(sessionId);
      //await deleteSession({ sessionId });
    //}
  } catch (err) {
    // session might already be expired, that's ok
  } finally {
    localStorage.removeItem('session_id');
    navigate('/');
  }
};


const handleManage = async () => {
  setCheckoutLoading("portal");
  try {
    const { url } = await createPortal({ userId: user._id });
    window.location.href = url;
  } catch (err) {
    setError("Failed to open billing portal");
  } finally {
    setCheckoutLoading(null);
  }
};

  const restoreHistory = (item) => {
    setSelectedCategory(item.category);
    setSelectedSituation(item.situationId);
    setSelectedTone(item.tone);
    setSelectedFormat(item.format);
    setContext(item.context);
    setResult(item.result);
    setStep(4);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth" }), 150);
  };

  const reset = () => {
    setStep(1); setSelectedCategory(null); setSelectedSituation(null);
    setSelectedTone("firm"); setSelectedFormat("script");
    setContext(""); setResult(null); setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div style={S.app}>
      <header style={S.header}>
        <div style={S.headerInner}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>⚡</span>
            <span style={S.logoText}>SayIt</span>
            <span style={S.logoBeta}>BETA</span>
          </div>
          <nav style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {history.length > 0 && (
              <span style={{ fontSize: 12, color: "#555", background: "#13131A", border: "1px solid #1E1E28", borderRadius: 20, padding: "4px 10px" }}>
                🕐 {history.length} saved
              </span>
            )}
            <button onClick={() => { setShowPricing(!showPricing); }}
              style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>
              Pricing
            </button>
            <button
              onClick={() => { setShowPricing(!showPricing); }}
              style={S.navBtnPrimary}
            >
              Get Full Access
            </button>
            <button
              onClick={() => handleLogout()}
              style={S.navBtnPrimary}
            >
              Logout
            </button>
          </nav>
        </div>
      </header>

      <HistoryPanel history={history} onRestore={restoreHistory} onClear={() => setHistory([])} />

      {/* Hero */}
      {step === 1 && !showPricing && (
        <section style={{ ...S.section, paddingTop: 80, paddingBottom: 60, textAlign: "center", maxWidth: 700, animation: "fadeUp 0.6s ease both" }}>
          <div style={S.heroBadge}>✦ No more awkward silence</div>
          <h1 style={S.heroTitle}>
            The exact words<br />
            <em style={{ fontStyle: "italic", color: "#C8A96E" }}>for hard conversations</em>
          </h1>
          <p style={{ fontSize: 17, color: "#777", lineHeight: 1.75, marginBottom: 36 }}>
            Tell me the situation. Get a ready-to-use script, email, or text —<br />
            tone variations, pushback responses, and a do/don't guide.
          </p>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 24, marginBottom: 36 }}>
            {[["42", "situations"], ["6", "tones"], ["4", "formats"], ["🔊", "voice"]].map(([n, l], i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <strong style={{ fontSize: 22, color: "#F0F0F5", fontWeight: 800 }}>{n}</strong>
                <span style={{ fontSize: 11, color: "#444", textTransform: "uppercase", letterSpacing: "0.1em" }}>{l}</span>
              </div>
            ))}
          </div>
          <button onClick={() => setStep(2)} style={S.heroBtn} className="hero-btn">
            Build My Script →
          </button>
          <p style={{ fontSize: 13, color: "#444", marginTop: 12, marginBottom: 32 }}>Free for everyday · Premium packs for high-stakes</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
            {mapped.map((s, i) => (
              <span key={i} style={{ fontSize: 12, color: "#444", border: "1px solid #1E1E28", borderRadius: 20, padding: "5px 12px" }}>{s}</span>
            ))}
          </div>
        </section>
      )}

      {/* Pricing */}
      {showPricing && (
        <section style={{ ...S.section, animation: "fadeUp 0.4s ease both" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <h2 style={S.sectionTitle}>High-Stakes Packs</h2>
            <p style={{ color: "#666", fontSize: 16 }}>Full playbooks for when it really matters</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 20, marginBottom: 32 }}>
            {PACKS.map(pack => (
            <div key={pack.id} style={{ ...S.packCard, borderColor: pack.popular ? pack.color : "#2a2a35", animation: pack.popular ? "glow 3s ease infinite" : "none" }}>
            {pack.popular && (
            <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", fontSize: 11, fontWeight: 700, color: "#0C0C0E", padding: "4px 14px", borderRadius: 20, background: pack.color, whiteSpace: "nowrap" }}>
            Most Popular
            </div>
            )}
        <div style={{ fontSize: 22, color: pack.color, marginBottom: 12 }}>✦</div>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 19, fontWeight: 700, color: "#F0F0F5", marginBottom: 6 }}>{pack.title}</h3>
        <div style={{ fontSize: 28, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: pack.color, marginBottom: 20 }}>{pack.price}</div>
        <div style={{ marginBottom: 24 }}>
        {pack.includes.map((item, i) => (
                <div key={i} style={{ fontSize: 13, color: "#666", padding: "6px 0", borderBottom: "1px solid #1a1a22" }}>✓ {item}</div>
            ))}
        </div>
        {userHasPack(user, pack.id) ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ fontSize: 12, color: "#4a9a6a", textAlign: "center", padding: "8px", background: "#0F1A12", borderRadius: 8, border: "1px solid #2a4a30" }}>
                    ✓ Active
                    </div>
                    <button
                    onClick={() => handleManage()}
                    style={{ width: "100%", border: "1px solid #E07A7A", borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 600, color: "#E07A7A", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", background: "transparent" }}>
                    Cancel Subscription
                    </button>
            </div>
                ) : (
                <button
                    onClick={() => handleGetPack(pack.id)}
                    style={{ width: "100%", border: "none", borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 600, color: "#0C0C0E", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", background: pack.color }}>
                    Get This Pack
                </button>
                )}
            </div>
            ))}
          </div>
          <button onClick={() => setShowPricing(false)} style={{ display: "block", margin: "0 auto", background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>← Back to generator</button>
        </section>
      )}

      {/* Step 2: Situation — only show accessible situations */}
      {step >= 2 && !showPricing && (
        <section style={{ ...S.section, animation: "fadeUp 0.4s ease both" }}>
          <div style={S.stepHeader}>
            <div style={S.stepNum}>01</div>
            <h2 style={S.stepTitle}>What's the situation?</h2>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
            {Object.entries(accessibleSituations).map(([key, cat]) => (
              <button key={key} onClick={() => { setSelectedCategory(key); setSelectedSituation(null); }}
                style={{ ...S.categoryTab, ...(selectedCategory === key ? { borderColor: cat.color, color: cat.color, background: "#1E1E28" } : {}) }}>
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
          {selectedCategory && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(175px, 1fr))", gap: 10 }}>
              {accessibleSituations[selectedCategory].situations
                //.filter(sit => accessibleIds.has(sit.id)) // ← only show accessible
                .map(sit => (
                  <button key={sit.id} className="sit-card"
                    onClick={() => { setSelectedSituation(sit.id); setStep(3); }}
                    style={{ ...S.situationCard, ...(selectedSituation === sit.id ? { border: "1px solid #C8A96E", background: "#1C1914" } : {}), transition: "all 0.2s" }}>
                    <span style={{ fontSize: 22 }}>{sit.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>{sit.label}</span>
                    {sit.premium && <span style={S.premiumTag}>PRO</span>}
                  </button>
                ))}
              {/* show locked situations greyed out */}
              
            </div>
          )}
        </section>
      )}

      {/* Step 3: Customize */}
      {step >= 3 && selectedSituation && !showPricing && (
        <section style={{ ...S.section, animation: "fadeUp 0.4s ease both" }}>
          <div style={S.stepHeader}>
            <div style={S.stepNum}>02</div>
            <h2 style={S.stepTitle}>Customize your script</h2>
          </div>

          <div style={{ marginBottom: 28 }}>
            <label style={S.groupLabel}>Tone</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
              {TONES.map(tone => (
                <button key={tone.id} className="tone-card" onClick={() => setSelectedTone(tone.id)}
                  style={{ ...S.toneCard, ...(selectedTone === tone.id ? { border: "1px solid #C8A96E", background: "#1C1914" } : {}), transition: "all 0.2s" }}>
                  <div style={{ fontSize: 18, marginBottom: 6 }}>{tone.emoji}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#E8E8EC", marginBottom: 3 }}>{tone.label}</div>
                  <div style={{ fontSize: 11, color: "#555" }}>{tone.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 28 }}>
            <label style={S.groupLabel}>Format</label>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {FORMATS.map(fmt => (
                <button key={fmt.id} onClick={() => setSelectedFormat(fmt.id)}
                  style={{ ...S.formatBtn, ...(selectedFormat === fmt.id ? { border: "1px solid #7A9EC8", color: "#7A9EC8", background: "#12161E" } : {}), transition: "all 0.2s" }}>
                  {fmt.icon} {fmt.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 28 }}>
            <label style={S.groupLabel}>Add context <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional — makes the script sharper)</span></label>
            <textarea value={context} onChange={e => setContext(e.target.value)}
              placeholder={`e.g., "We've worked together 2 years, they interrupted me in the meeting again, I want to keep the relationship"`}
              style={S.textarea} rows={3} />
          </div>

          <button onClick={generateScript} disabled={loading}
            style={{ ...S.generateBtn, ...(loading ? { opacity: 0.7, cursor: "not-allowed" } : {}) }} className="gen-btn">
            {loading ? (
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <span style={S.spinner} /> Writing your script...
              </span>
            ) : "Generate My Script →"}
          </button>

          {loading && (
            <div style={{ height: 3, background: "#1E1E28", borderRadius: 2, marginTop: 12, overflow: "hidden" }}>
              <div style={{ height: "100%", width: "70%", background: "linear-gradient(90deg, transparent, #C8A96E, transparent)", backgroundSize: "200% 100%", animation: "shimmer 1.2s linear infinite" }} />
            </div>
          )}

          {error && <div style={{ marginTop: 12, padding: 12, background: "#1C1014", border: "1px solid #5a2a2a", borderRadius: 8, color: "#E07A7A", fontSize: 14 }}>{error}</div>}
        </section>
      )}

      {/* Result */}
      {step >= 4 && result && !showPricing && (
        <section style={{ ...S.section, animation: "fadeUp 0.4s ease both" }} ref={resultRef}>
          <div style={S.stepHeader}>
            <div style={S.stepNum}>03</div>
            <h2 style={S.stepTitle}>Your script is ready</h2>
          </div>
          <ScriptResult result={result} tone={selectedTone} format={selectedFormat} />
          <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
            <button onClick={reset} style={{ background: "transparent", border: "1px solid #2a2a35", borderRadius: 10, padding: "11px 18px", color: "#888", cursor: "pointer", fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>← New Script</button>
            <button onClick={() => { setStep(3); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              style={{ background: "transparent", border: "1px solid #2a2a35", borderRadius: 10, padding: "11px 18px", color: "#888", cursor: "pointer", fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>
              ✏️ Tweak Settings
            </button>
            <button onClick={() => navigate("/pricingpage", { state: { sessionId } })}
              style={{ flex: 1, background: "#1C1914", border: "1px solid #C8A96E", borderRadius: 10, padding: "11px 18px", color: "#C8A96E", cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
              🔓 Unlock High-Stakes Packs
            </button>
          </div>
        </section>
      )}

      <footer style={{ borderTop: "1px solid #1E1E28", padding: "40px 24px", textAlign: "center" }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: "#2a2a35", marginBottom: 8 }}>⚡ SayIt</div>
        <p style={{ fontSize: 13, color: "#252525" }}>Say what needs to be said. Exactly.</p>
      </footer>
    </div>
  );
}

const S = {
  app: { paddingTop: "220px",minHeight: "100vh", background: "#0C0C0E", color: "#E8E8EC", fontFamily: "'DM Sans', sans-serif" },
  header: { borderBottom: "1px solid #1E1E28", padding: "0 24px", position: "sticky", top: 0, background: "rgba(12,12,14,0.96)", backdropFilter: "blur(16px)", zIndex: 100 },
  headerInner: { maxWidth: 960, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 60 },
  logoText: { fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: "#E8E8EC" },
  logoBeta: { fontSize: 10, fontWeight: 600, color: "#C8A96E", border: "1px solid #C8A96E", borderRadius: 4, padding: "2px 6px", letterSpacing: "0.08em" },
  navBtnPrimary: { background: "#C8A96E", border: "none", color: "#0C0C0E", cursor: "pointer", fontSize: 13, fontWeight: 600, padding: "8px 16px", borderRadius: 8, fontFamily: "'DM Sans', sans-serif" },
  section: { width:"1000px", margin: "0 auto", padding: "48px 24px" },
  heroBadge: { display: "inline-block", fontSize: 12, color: "#C8A96E", letterSpacing: "0.12em", fontWeight: 500, marginBottom: 28, textTransform: "uppercase" },
  heroTitle: { fontFamily: "'Playfair Display', serif", fontSize: "clamp(42px, 7vw, 70px)", fontWeight: 900, lineHeight: 1.05, color: "#F0F0F5", marginBottom: 20 },
  heroBtn: { background: "#C8A96E", color: "#0C0C0E", border: "none", borderRadius: 12, padding: "16px 40px", fontSize: 16, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s", display: "inline-block" },
  stepHeader: { display: "flex", alignItems: "baseline", gap: 16, marginBottom: 32 },
  stepNum: { fontFamily: "'Playfair Display', serif", fontSize: 48, fontWeight: 900, color: "#2a2a35", lineHeight: 1 },
  stepTitle: { fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700, color: "#F0F0F5" },
  categoryTab: { background: "#15151C", border: "1px solid #2a2a35", borderRadius: 24, padding: "8px 18px", color: "#666", cursor: "pointer", fontSize: 14, fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s" },
  situationCard: { background: "#13131A", border: "1px solid #1E1E28", borderRadius: 12, padding: "16px", cursor: "pointer", textAlign: "left", color: "#C0C0CC", fontFamily: "'DM Sans', sans-serif", display: "flex", flexDirection: "column", gap: 8, position: "relative" },
  premiumTag: { position: "absolute", top: 10, right: 10, fontSize: 9, fontWeight: 700, color: "#C8A96E", border: "1px solid #C8A96E", borderRadius: 4, padding: "2px 5px", letterSpacing: "0.08em" },
  groupLabel: { display: "block", fontSize: 11, fontWeight: 600, color: "#555", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 12 },
  toneCard: { background: "#13131A", border: "1px solid #1E1E28", borderRadius: 10, padding: "14px", cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans', sans-serif" },
  formatBtn: { background: "#13131A", border: "1px solid #1E1E28", borderRadius: 10, padding: "10px 18px", cursor: "pointer", color: "#666", fontSize: 14, fontFamily: "'DM Sans', sans-serif" },
  textarea: { width: "100%", background: "#13131A", border: "1px solid #1E1E28", borderRadius: 10, padding: "14px 16px", color: "#C0C0CC", fontSize: 14, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6, resize: "vertical", transition: "border-color 0.2s", outline: "none" },
  generateBtn: { width: "100%", background: "#C8A96E", border: "none", borderRadius: 12, padding: "18px", fontSize: 16, fontWeight: 600, color: "#0C0C0E", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", marginTop: 8, transition: "all 0.2s" },
  spinner: { width: 16, height: 16, border: "2px solid rgba(12,12,14,0.3)", borderTopColor: "#0C0C0E", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" },
  sectionTitle: { fontFamily: "'Playfair Display', serif", fontSize: 34, fontWeight: 700, color: "#F0F0F5", marginBottom: 10 },
  packCard: { background: "#13131A", border: "1px solid #1E1E28", borderRadius: 16, padding: "28px 24px", position: "relative" },
  resultContainer: { background: "#13131A", border: "1px solid #2a2a35", borderRadius: 16, overflow: "hidden" },
  resultHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid #1E1E28" },
  badge1: { fontSize: 10, fontWeight: 700, color: "#7A9EC8", border: "1px solid #7A9EC8", borderRadius: 4, padding: "2px 7px", letterSpacing: "0.1em" },
  badge2: { fontSize: 10, fontWeight: 700, color: "#C8A96E", border: "1px solid #C8A96E", borderRadius: 4, padding: "2px 7px", letterSpacing: "0.1em" },
  copyBtn: { background: "transparent", border: "1px solid #2a2a35", borderRadius: 8, padding: "6px 14px", color: "#666", cursor: "pointer", fontSize: 13, fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s" },
  tabs: { display: "flex", borderBottom: "1px solid #1E1E28", overflowX: "auto" },
  tab: { background: "none", border: "none", padding: "12px 18px", color: "#555", cursor: "pointer", fontSize: 13, fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap", borderBottom: "2px solid transparent", transition: "all 0.2s" },
  tabActive: { color: "#C8A96E", borderBottomColor: "#C8A96E" },
  scriptLabel: { color: "#C8A96E", fontWeight: 600, fontSize: 11, marginBottom: 4, marginTop: 16, textTransform: "uppercase", letterSpacing: "0.1em" },
  scriptLine: { color: "#D0D0DC", fontSize: 15, lineHeight: 1.85, marginBottom: 2 },
  listItem: { fontSize: 13, color: "#C0C0CC", padding: "8px 0", borderBottom: "1px solid #1a1a22", lineHeight: 1.5 },
};